import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { 
  TrendingUp, 
  AlertCircle, 
  Lightbulb,
  User,
  FileText,
  Sparkles
} from 'lucide-react';

const TeamsValueProp = () => {
  return (
    <section 
      id="value-prop"
      className="py-24 bg-slate-900"
      data-testid="teams-value-prop"
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            What you see as a manager
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Real-time signals and evidence that make performance conversations easier.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Team Health Signals Card */}
          <Card 
            className="bg-slate-950 border-slate-800"
            data-testid="team-signals-preview"
          >
            <CardHeader className="border-b border-slate-800">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                  Team Health Signals
                </CardTitle>
                <Badge variant="outline" className="border-slate-700 text-slate-400">
                  Anonymized
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Signal bars */}
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-300 text-sm">Confidence</span>
                    <div className="flex items-center gap-2">
                      <span className="text-green-400 text-sm font-medium">High</span>
                      <span className="text-slate-500 text-xs">Last 7 days</span>
                    </div>
                  </div>
                  <Progress value={80} className="h-2 bg-slate-800" />
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-300 text-sm">Workload</span>
                    <div className="flex items-center gap-2">
                      <span className="text-amber-400 text-sm font-medium">Moderate</span>
                      <span className="text-amber-400 text-xs">+12% from last week</span>
                    </div>
                  </div>
                  <Progress value={55} className="h-2 bg-slate-800" />
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-300 text-sm">Blockers</span>
                    <div className="flex items-center gap-2">
                      <span className="text-blue-400 text-sm font-medium">Low</span>
                      <span className="text-slate-500 text-xs">2 mentioned</span>
                    </div>
                  </div>
                  <Progress value={20} className="h-2 bg-slate-800" />
                </div>
              </div>

              {/* Insight callout */}
              <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <Lightbulb className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-200">
                  "3 team members mentioned unclear priorities this week"
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Individual Evidence Card */}
          <Card 
            className="bg-slate-950 border-slate-800"
            data-testid="individual-evidence-preview"
          >
            <CardHeader className="border-b border-slate-800">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <User className="w-5 h-5 text-indigo-400" />
                  Individual Evidence
                </CardTitle>
                <Badge variant="outline" className="border-slate-700 text-slate-400">
                  For Reviews
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Sample team member */}
              <div className="p-4 bg-slate-900/50 border border-slate-800 rounded-lg mb-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                      <span className="text-white font-medium">SC</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">Sarah Chen</p>
                      <p className="text-slate-500 text-sm">Senior Engineer</p>
                    </div>
                  </div>
                  <Badge className="bg-slate-800 text-slate-300">Q1 2026</Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-2xl font-bold text-white">12</p>
                    <p className="text-slate-400 text-xs">Achievements logged</p>
                  </div>
                  <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-2xl font-bold text-white">8</p>
                    <p className="text-slate-400 text-xs">Skills demonstrated</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
                  <AlertCircle className="w-4 h-4 text-amber-400" />
                  <span>Growth area: <span className="text-slate-300">Stakeholder management</span> (self-identified)</span>
                </div>
                
                <Button 
                  className="w-full bg-indigo-600 hover:bg-indigo-500"
                  data-testid="generate-review-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Review Summary
                </Button>
              </div>
              
              {/* Additional team members hint */}
              <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
                <div className="flex -space-x-2">
                  <div className="w-6 h-6 rounded-full bg-slate-700 border-2 border-slate-950" />
                  <div className="w-6 h-6 rounded-full bg-slate-700 border-2 border-slate-950" />
                  <div className="w-6 h-6 rounded-full bg-slate-700 border-2 border-slate-950" />
                </div>
                <span>+ 4 more team members</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default TeamsValueProp;
